import React, { useState } from 'react';
import { Button } from '../../components/Buttons/Button';
import { useMutation } from '@tanstack/react-query';
import { showErrorToast, showSuccessToast } from '../../components/ui/Toast';
import axios from '../../services/axios';
import OTPInput from '../../components/Inputs/OTPInput';
import { BackButton } from '../../components/utility/Header';
import { digitsRegex, lowerCaseRegex, specialCharRegex, upperCaseRegex } from '../../utility/regex';
import LoaderModal from '../../components/Loaders/LoaderModal';
import { InputField } from '../../components/Inputs/InputField';
import { forgotPasswordCandidate, resetPasswordCandidate, verifyPassOTPCandidate } from '../../services/auth.candidate.service';
import { forgotPassword, resetPassword, verifyPassOTP } from '../../services/auth.service';

const ForgotPassword = ({ onBack, role, isModal = false, setIsLoading = () => { } }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Request OTP mutation
  const requestOtpMutation = useMutation({
    mutationFn: async (email) => {
      const response = await (role === "Candidate" ? forgotPasswordCandidate(email) : forgotPassword(email));
      return response.data;
    },
    onMutate: () => {
      isModal && setIsLoading(true)
    },
    onSuccess: (data) => {
      isModal && setIsLoading(false)
      showSuccessToast('OTP sent to your email');
      setStep('otp');
    },
    onError: (error) => {
      isModal && setIsLoading(false)
      showErrorToast(error.response?.data?.message || 'Failed to send OTP. Please try again.');
    }
  });

  // Verify OTP mutation
  const verifyOtpMutation = useMutation({
    mutationFn: async () => {
      const response = await (role === "Candidate" ? verifyPassOTPCandidate(email,otp) : verifyPassOTP(email, otp));
      return response.data;
    },
    onMutate: () => {
      isModal && setIsLoading(true)
    },
    onSuccess: (data) => {
      isModal && setIsLoading(false)
      showSuccessToast('OTP verified successfully');
      setStep('newPassword');
    },
    onError: (error) => {
      isModal && setIsLoading(false)
      showErrorToast(error.response?.data?.message || 'Invalid OTP. Please try again.');
    }
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const response = await (role === "Candidate" ? resetPasswordCandidate(email,otp,password) : resetPassword(email,otp,password)) ;
      return response.data;
    },
    onMutate: () => {
      isModal && setIsLoading(true)
    },
    onSuccess: (data) => {
      isModal && setIsLoading(false)
      showSuccessToast('Password reset successfully');
      setTimeout(() => onBack(), 2000);
    },
    onError: (error) => {
      isModal && setIsLoading(false)
      showErrorToast(error.response?.data?.message || 'Failed to reset password. Please try again.');
    }
  });

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      showErrorToast('Please enter your email');
      return;
    }
    requestOtpMutation.mutate(email);
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    if (!otp) {
      showErrorToast('Please enter OTP');
      return;
    }
    verifyOtpMutation.mutate();
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      showErrorToast('Please fill in all fields');
      return;
    }
    if (password.length < 8) {
      showErrorToast('Password must be at least 8 characters long');
      return;
    }
    if (!upperCaseRegex.test(password)) {
      showErrorToast('Password must contain at least one uppercase letter');
      return;
    }
    if (!lowerCaseRegex.test(password)) {
      showErrorToast('Password must contain at least one lowercase letter');
      return;
    }
    if (!digitsRegex.test(password)) {
      showErrorToast('Password must contain at least one number');
      return;
    }
    if (!specialCharRegex.test(password)) {
      showErrorToast('Password must contain at least one special character');
      return;
    }
    if (password !== confirmPassword) {
      showErrorToast('Passwords do not match');
      return;
    }
    resetPasswordMutation.mutate();
  };

  const handleOtpChange = (value) => {
    setOtp(value);
  };

  return (
    <div className="w-full">
      {((requestOtpMutation.isPending || verifyOtpMutation.isPending || resetPasswordMutation.isPending) && !isModal) && <LoaderModal />}
      {!isModal && <div className='flex cursor-pointer gap-4 my-4 items-center'
        onClick={onBack}>
        <BackButton />
        <p className='typography-body'>
          Back to Login
        </p>
      </div>}



      {!isModal && <h2 className="mb-6">Reset Password</h2>}

      {step === 'email' && (
        <form onSubmit={handleEmailSubmit}>
          <InputField
            id="reset-email"
            type="email"
            label="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            type="submit"
            variant="primary"
            className="mt-4"
            disabled={requestOtpMutation.isPending}
          >
            {requestOtpMutation.isPending ? 'Sending...' : 'Send OTP'}
          </Button>
        </form>
      )}
      {step === 'otp' && (
        <form onSubmit={handleOtpSubmit}>
          <div className="space-y-4">
            <div className={isModal ? "text-start" : "text-center"}>
              <label className="block mb-4 typography-body">
                Enter the verification code sent to your email
              </label>
              {role === 'Candidate' && (
                <p className="typography-small-p text-font-gray mb-4">
                  Didn&apos;t receive the code? Please check your spam or junk folder.
                </p>
              )}
              <OTPInput
                length={6}
                value={otp}
                onChange={handleOtpChange}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              className=" mt-6"
              disabled={verifyOtpMutation.isPending || otp.length !== 6}
            >
              {verifyOtpMutation.isPending ? 'Verifying...' : 'Verify OTP'}
            </Button>
          </div>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={handlePasswordSubmit}>
          <div className="space-y-4">
            <InputField
              id="new-password"
              type="password"
              label="New Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}

            />
            <InputField
              id="confirm-password"
              type="password"
              label="Confirm Password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}

            />
          </div>
          <Button
            type="submit"
            variant="primary"
            className=" mt-4"
            disabled={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default ForgotPassword;